import { ComponentType, ReactElement } from 'react'

import { Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import { Flex, Icon } from 'components/core'
import TrackedLink from 'components/Links/TrackedLink'
import MarkdownRenderer from 'components/MarkdownRenderer/MarkdownRenderer'
import { TooltipComponentType } from 'configuration/fieldCreators/localTypes'
import { getPath } from 'routing/pagePaths'

import { DataSource, DataSourceMetric } from '../types'

import FieldTooltip, { FieldTooltipProps } from './FieldTooltip'
import { MetricFormula } from './MetricFormulaList'
import * as styles from './styles.scss'

export type MetricTooltipAlertType = 'disallowed'

export interface MetricTooltipProps extends Omit<FieldTooltipProps, 'title'> {
    metric: DataSourceMetric
    dataSource: DataSource
    customTooltip?: TooltipComponentType
}

export default function MetricTooltip({
    metric,
    dataSource,
    children,
    placement,
    align,
    alertMessage,
    alertType,
    zIndex,
    metadata,
    customTooltip,
    ...otherProps
}: MetricTooltipProps): ReactElement {
    const { t } = useTranslation()
    const name = t('common:glossary', 'Glossary')

    const hasFormula = !!metric.metadata_formula
    const metricPath = `${getPath('metricsGlossaryPage')}?group=${
        dataSource.data_source_group.slug
    }&dataSource=${dataSource.slug}&metric=${metric.slug}`

    const canLinkToGlossary = dataSource.is_selectable_in_dashboards

    const title = (
        <Flex gap={32}>
            <Flex style={{ flex: 1 }} alignItems="normal">
                <span className={styles['tooltip-title']}>
                    {metric.display_name}
                </span>
            </Flex>
            {canLinkToGlossary && (
                <Flex>
                    <TrackedLink
                        target="_blank"
                        to={metricPath}
                        from="MetricTooltip"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '12.6px',
                        }}
                    >
                        {name}
                        <Icon name="LINK_EXTERNAL" size={12} />
                    </TrackedLink>
                </Flex>
            )}
        </Flex>
    )

    const body = (
        <>
            {metric.definition && (
                <Typography.Text
                    className={styles['tooltip-metric-definition']}
                >
                    <MarkdownRenderer markdown={metric.definition} />
                </Typography.Text>
            )}
            {hasFormula && (
                <div className={styles['tooltip-formula-wrapper']}>
                    <MetricFormula metric={metric} />
                </div>
            )}
        </>
    )

    const TooltipType = (customTooltip ??
        FieldTooltip) as ComponentType<FieldTooltipProps>

    return (
        <TooltipType
            {...otherProps}
            title={title}
            body={body}
            placement={placement}
            align={align}
            zIndex={zIndex}
            alertMessage={alertMessage}
            alertType={alertType}
            metadata={metadata}
        >
            <div>{children ?? '-'}</div>
        </TooltipType>
    )
}
